import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, MessagesSquare, Paperclip, Send } from "lucide-react"
import { type FormEvent, useRef, useState } from "react"
import { api, ErrorApi, pedir } from "@/api/cliente"
import { comoFechaHora } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * Bandeja y conversación del buzón interno.
 *
 * **Un solo componente para el personal y para el importador.** Los datos, el
 * orden y las reglas son idénticos —el servidor ya recorta por cliente— y mantener
 * dos copias garantizaría que una se quedara atrás. Lo único que cambia entre las
 * dos vistas es el texto de cabecera, que llega por prop.
 *
 * La bandeja se ordena por última actividad y eso viene del servidor: reordenar
 * aquí daría una lista distinta de la que el backend pagina.
 */

interface HiloResumen {
  id: number
  cliente_id: number
  expediente_id: number | null
  asunto: string
  cerrado: boolean
  creado_por: number
  creado_en: string
  actualizado_en: string
}

interface Adjunto {
  id: number
  nombre_original: string
  mime: string
  bytes: number
  creado_en: string
}

interface MensajeHilo {
  id: number
  hilo_id: number
  autor_id: number
  cuerpo: string
  creado_en: string
  adjuntos: Adjunto[]
}

interface HiloDetalle extends HiloResumen {
  mensajes: MensajeHilo[]
}

interface PaginaHilos {
  total: number
  limit: number
  offset: number
  items: HiloResumen[]
}

export function Conversacion({ titulo, subtitulo }: { titulo: string; subtitulo: string }) {
  const [activo, setActivo] = useState<number | null>(null)
  const [cuerpo, setCuerpo] = useState("")
  const entrada = useRef<HTMLInputElement>(null)
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()

  const bandeja = useQuery({
    queryKey: ["hilos"],
    queryFn: () => api.get<PaginaHilos>("/api/v1/hilos?limit=50"),
    // El buzón se mira esperando respuesta; el sondeo es el que hace que llegue
    // sin recargar. La tabla NO se anima, o temblaría cada minuto.
    refetchInterval: 60_000,
  })

  const hilo = useQuery({
    queryKey: ["hilo", activo],
    queryFn: () => api.get<HiloDetalle>(`/api/v1/hilos/${activo}`),
    enabled: activo !== null,
    refetchInterval: 60_000,
  })

  const responder = useMutation({
    mutationFn: () => {
      const datos = new FormData()
      datos.append("cuerpo", cuerpo)
      for (const archivo of entrada.current?.files ?? []) datos.append("archivos", archivo)
      return pedir<MensajeHilo>(`/api/v1/hilos/${activo}/mensajes`, {
        method: "POST",
        body: datos,
      })
    },
    onSuccess: () => {
      setCuerpo("")
      if (entrada.current) entrada.current.value = ""
      clienteQuery.invalidateQueries({ queryKey: ["hilo", activo] })
      // La bandeja cambia de orden: este hilo sube al principio.
      clienteQuery.invalidateQueries({ queryKey: ["hilos"] })
    },
  })

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (cuerpo.trim()) responder.mutate()
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold">{titulo}</h1>
        <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
          {subtitulo}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-[18rem_1fr]">
        <div className="superficie">
          {bandeja.isPending && (
            <p className="p-4 text-sm" style={{ color: "var(--fg-muted)" }}>
              Cargando conversaciones…
            </p>
          )}
          {bandeja.isError && (
            <p role="alert" className="p-4 text-sm" style={{ color: "var(--destructive)" }}>
              No se pudo cargar la bandeja
            </p>
          )}
          {bandeja.data?.items.length === 0 && (
            <div
              className="flex flex-col items-center gap-2 p-8"
              style={{ color: "var(--fg-muted)" }}
            >
              <MessagesSquare size={22} aria-hidden="true" />
              <p className="text-sm">No hay conversaciones</p>
            </div>
          )}
          <ul>
            {bandeja.data?.items.map((h) => (
              <li key={h.id}>
                <button
                  type="button"
                  onClick={() => setActivo(h.id)}
                  className="w-full border-b p-3 text-left text-sm last:border-b-0"
                  style={{
                    borderColor: "var(--border)",
                    backgroundColor: activo === h.id ? "var(--surface-elevated)" : "transparent",
                  }}
                >
                  <span className="block font-medium">{h.asunto}</span>
                  <span className="tabular block text-xs" style={{ color: "var(--fg-muted)" }}>
                    {comoFechaHora(h.actualizado_en)}
                    {h.cerrado ? " · cerrado" : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* ⚠ **La columna derecha lleva caja porque la izquierda la lleva.** Sin
            ella, media pantalla estaba recuadrada y la otra media caía sobre el
            lienzo: se leía como si la conversación no hubiera terminado de cargar.
            En una rejilla de dos columnas, que una tenga límite y la otra no es la
            asimetría que más se nota. */}
        <div className="tarjeta">
          {activo === null && (
            <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
              Elija una conversación
            </p>
          )}

          {hilo.data && (
            <>
              <h2 className="mb-4 font-semibold">{hilo.data.asunto}</h2>

              <ol className="mb-6 space-y-3">
                {hilo.data.mensajes.map((m) => {
                  const mio = m.autor_id === usuario?.id
                  return (
                    <li
                      key={m.id}
                      className="rounded-[var(--radius-tarjeta)] border p-3"
                      style={{
                        borderColor: "var(--border)",
                        backgroundColor: mio ? "var(--surface-elevated)" : "var(--surface)",
                      }}
                    >
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-xs font-medium">{mio ? "Usted" : "SETIECA"}</span>
                        <span className="tabular text-xs" style={{ color: "var(--fg-muted)" }}>
                          {comoFechaHora(m.creado_en)}
                        </span>
                      </div>
                      {/* Texto plano, nunca `dangerouslySetInnerHTML`: el cuerpo
                          lo escribe un usuario y Biome lo marca como error. */}
                      <p className="whitespace-pre-wrap text-sm">{m.cuerpo}</p>

                      {m.adjuntos.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {m.adjuntos.map((a) => (
                            <li key={a.id} className="flex items-center gap-1.5 text-xs">
                              <Paperclip size={14} aria-hidden="true" />
                              {a.nombre_original}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  )
                })}
              </ol>

              {hilo.data.cerrado ? (
                <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                  Esta conversación está cerrada.
                </p>
              ) : (
                // Separado del hilo por una línea: los mensajes de arriba son
                // tarjetas y esto es el sitio donde se escribe, no un mensaje más.
                <form
                  onSubmit={enviar}
                  className="border-t pt-4"
                  style={{ borderColor: "var(--border)" }}
                >
                  <label htmlFor="cuerpo" className="mb-1 block text-xs font-medium uppercase">
                    Responder
                  </label>
                  <textarea
                    id="cuerpo"
                    value={cuerpo}
                    onChange={(e) => setCuerpo(e.target.value)}
                    rows={3}
                    className="w-full rounded-[var(--radius-control)] border p-2 text-sm"
                    style={{
                      borderColor: "var(--border)",
                      backgroundColor: "var(--background)",
                      color: "var(--fg)",
                    }}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <label
                      className="flex h-9 cursor-pointer items-center gap-2 rounded-[var(--radius-control)] border px-3 text-sm"
                      style={{ borderColor: "var(--border)" }}
                    >
                      <Paperclip size={16} aria-hidden="true" />
                      Adjuntar
                      <input
                        ref={entrada}
                        type="file"
                        multiple
                        className="sr-only"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                      />
                    </label>
                    <button
                      type="submit"
                      disabled={!cuerpo.trim() || responder.isPending}
                      className="flex h-9 items-center gap-2 rounded-[var(--radius-control)] px-3 text-sm font-medium disabled:opacity-50"
                      style={{ backgroundColor: "var(--primary)", color: "var(--primary-fg)" }}
                    >
                      <Send size={16} aria-hidden="true" />
                      {responder.isPending ? "Enviando…" : "Enviar"}
                    </button>
                  </div>

                  {responder.isError && (
                    <p
                      role="alert"
                      className="mt-2 flex items-start gap-2 text-sm"
                      style={{ color: "var(--destructive)" }}
                    >
                      <AlertCircle size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
                      {responder.error instanceof ErrorApi
                        ? responder.error.message
                        : "No se pudo enviar el mensaje"}
                    </p>
                  )}
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
